import { useEffect, useState } from "react";
import PageShell from "@/components/PageShell";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link } from "react-router-dom";
import {
  Loader2, Users, TrendingUp, DollarSign, CreditCard, Heart, Gift,
  UserCheck, UserX, Sparkles, RefreshCw, ArrowLeft,
} from "lucide-react";

const money = (n, cur = "USD") =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: cur }).format(n || 0);

function StatCard({ icon: Icon, label, value, sub, testid, accent }) {
  return (
    <div data-testid={testid}
      className="bg-card border border-border rounded-sm p-5 flex flex-col gap-2 hover:border-primary/50 transition-colors">
      <div className="flex items-center gap-2 text-muted-foreground">
        <Icon className={`w-4 h-4 ${accent ? "text-primary" : ""}`} />
        <span className="text-[10px] uppercase tracking-widest font-mono">{label}</span>
      </div>
      <div className={`font-serif text-3xl leading-none ${accent ? "text-primary" : "text-foreground"}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground">{sub}</div>}
    </div>
  );
}

export default function Reconciliation() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/reconciliation")
      .then((r) => { setData(r.data); setErr(null); })
      .catch((e) => setErr(formatApiError(e.response?.data?.detail) || "Failed to load reconciliation"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (user && user.role === "admin") load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  if (user === null)
    return <PageShell><div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div></PageShell>;
  if (!user || user.role !== "admin") return <Navigate to="/dashboard" replace />;

  const m = data?.members || {};
  const p = data?.projections || {};
  const rev = data?.revenue || {};
  const sup = data?.supporters || {};
  const rows = data?.recent_transactions || [];

  return (
    <PageShell>
      <div className="max-w-7xl mx-auto px-6 py-12" data-testid="reconciliation-page">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <span className="text-xs uppercase tracking-widest font-mono text-primary">Admin · Finance</span>
            <h1 className="font-serif text-5xl mt-2">Reconciliation</h1>
            <p className="text-sm text-muted-foreground mt-2">Memberships, revenue collected, and forward projections.</p>
          </div>
          <div className="flex items-center gap-2">
            <Link to="/admin" data-testid="reconciliation-back"
              className="flex items-center gap-2 border border-border px-3 py-2 rounded-sm text-sm text-muted-foreground hover:border-primary hover:text-primary transition-colors">
              <ArrowLeft className="w-4 h-4" /> Content Editor
            </Link>
            <button onClick={load} data-testid="reconciliation-refresh"
              className="flex items-center gap-2 bg-primary text-primary-foreground px-4 py-2 rounded-sm text-sm hover:-translate-y-[1px] transition-transform">
              <RefreshCw className="w-4 h-4" /> Refresh
            </button>
          </div>
        </div>

        {loading && <div className="p-20 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-primary" /></div>}
        {err && !loading && <div className="p-8 text-center text-destructive-foreground" data-testid="reconciliation-error">{err}</div>}

        {data && !loading && (
          <div className="space-y-10">
            {/* Projections */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Recurring revenue projection</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <StatCard icon={TrendingUp} label="Projected MRR" value={money(p.mrr)} sub={`${m.active_members} active × ${money(p.monthly_price)}/mo`} testid="stat-mrr" accent />
                <StatCard icon={TrendingUp} label="Projected ARR" value={money(p.arr)} sub="MRR × 12 months" testid="stat-arr" accent />
                <StatCard icon={Gift} label="Free months owed" value={m.free_months_owed} sub="Earned via referrals" testid="stat-free-months" />
              </div>
            </section>

            {/* Revenue collected */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Revenue collected to date</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={DollarSign} label="Total collected" value={money(rev.total_collected)} sub={`${rev.subscription_payments + rev.credit_orders} paid orders`} testid="stat-total-collected" accent />
                <StatCard icon={CreditCard} label="Subscriptions" value={money(rev.subscription_revenue)} sub={`${rev.subscription_payments} payments`} testid="stat-sub-revenue" />
                <StatCard icon={Sparkles} label="AI credit packs" value={money(rev.credits_revenue)} sub={`${rev.credit_orders} orders`} testid="stat-credits-revenue" />
                <StatCard icon={Sparkles} label="AI credits sold" value={(rev.credits_sold || 0).toLocaleString()} sub="Total credits purchased" testid="stat-credits-sold" />
              </div>
            </section>

            {/* Membership */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Membership</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Users} label="Total accounts" value={m.total_users} sub={`${m.admins} admin`} testid="stat-total-users" />
                <StatCard icon={UserCheck} label="Active members" value={m.active_members} sub="Paying subscribers" testid="stat-active-members" accent />
                <StatCard icon={UserX} label="Canceled" value={m.canceled} sub="Lapsed subscriptions" testid="stat-canceled" />
                <StatCard icon={TrendingUp} label="New (30 days)" value={m.new_last_30_days} sub="Recent signups" testid="stat-new-30d" />
              </div>
            </section>

            {/* Supporters */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Supporters</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard icon={Heart} label="Total supporters" value={sup.total} sub="Bought AI credits" testid="stat-supporters" accent />
                <StatCard icon={Heart} label="Supporter tier" value={sup.supporter} sub="≥ 1 credit" testid="stat-tier-supporter" />
                <StatCard icon={Heart} label="Champion tier" value={sup.champion} sub="≥ 500 credits" testid="stat-tier-champion" />
                <StatCard icon={Heart} label="Patron tier" value={sup.patron} sub="≥ 1000 credits" testid="stat-tier-patron" />
              </div>
            </section>

            {/* Recent transactions */}
            <section>
              <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground mb-3">Recent transactions</h2>
              <div className="border border-border rounded-sm overflow-hidden" data-testid="recent-transactions">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/50 text-left text-[10px] uppercase tracking-widest font-mono text-muted-foreground">
                      <th className="px-4 py-3">Date</th>
                      <th className="px-4 py-3">Member</th>
                      <th className="px-4 py-3">Type</th>
                      <th className="px-4 py-3 text-right">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && (
                      <tr data-testid="recent-empty"><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No paid transactions yet.</td></tr>
                    )}
                    {rows.map((t, i) => (
                      <tr key={i} data-testid={`txn-row-${i}`} className="border-t border-border hover:bg-muted/30">
                        <td className="px-4 py-3 text-muted-foreground whitespace-nowrap">{t.date ? new Date(t.date).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3">
                          <div className="text-foreground">{t.member}</div>
                          {t.name && <div className="text-xs text-muted-foreground">{t.name}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-xs font-mono px-2 py-0.5 rounded-full border ${t.kind === "credits" ? "border-primary/50 text-primary" : "border-border text-muted-foreground"}`}>
                            {t.kind === "credits" ? `${t.credits} credits` : "membership"}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-right font-mono text-foreground whitespace-nowrap">{money(t.amount, t.currency)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          </div>
        )}
      </div>
    </PageShell>
  );
}
